import { ReplaceStep, Step } from "@tiptap/pm/transform";
import { TextSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { Slice } from "@tiptap/pm/model";
import { Extension, Mark, getMarkRange, getMarksBetween, isMarkActive, mergeAttributes } from "@tiptap/core";

const LOG_ENABLED = false;

export const MARK_DELETION = "deletion";
export const MARK_INSERTION = "insertion";
export const EXTENSION_NAME = "trackchange";

// Track Change Operations
export const TRACK_COMMAND_ACCEPT = "accept";
export const TRACK_COMMAND_ACCEPT_ALL = "accept-all";
export const TRACK_COMMAND_REJECT = "reject";
export const TRACK_COMMAND_REJECT_ALL = "reject-all";
export const TRACK_COMMAND_ACCEPT_BY_SESSION = "accept-by-session";
export const TRACK_COMMAND_REJECT_BY_SESSION = "reject-by-session";

// Marks created during a scoped track-changes session (e.g. a Quick Edit) carry this
// attribute so accept/reject can be limited to that session and never touch unrelated marks.
export const SESSION_ID_ATTR = "data-op-edit-session-id";

// insert mark
export const InsertionMark = Mark.create({
  name: MARK_INSERTION,
  addAttributes() {
    return {
      "data-op-user-id": {
        type: "string",
        default: () => "",
      },
      "data-op-user-nickname": {
        type: "string",
        default: () => "",
      },
      "data-op-date": {
        type: "string",
        default: () => "",
      },
      [SESSION_ID_ATTR]: {
        type: "string",
        default: () => "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "insert" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["insert", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

// delete mark
export const DeletionMark = Mark.create({
  name: MARK_DELETION,
  addAttributes() {
    return {
      "data-op-user-id": {
        type: "string",
        default: () => "",
      },
      "data-op-user-nickname": {
        type: "string",
        default: () => "",
      },
      "data-op-date": {
        type: "string",
        default: () => "",
      },
      [SESSION_ID_ATTR]: {
        type: "string",
        default: () => "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "delete" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["delete", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

// save the ime-mode status, when input chinese char, the extension needs to deal the change with a special strategy
// TODO: Is it necessary to save these two variable into a tiptap instance when someone open two editor
const IME_STATUS_NORMAL = 0;
const IME_STATUS_START = 1;
const IME_STATUS_CONTINUE = 2;
const IME_STATUS_FINISHED = 3;
let composingStatus = 0; // 0: normal，1: start with first chat, 2: continue input, 3: finished by confirm or cancel with chars applied
let isStartChineseInput = false;

// get self extension instance by name
const getSelfExt = (editor) => editor.extensionManager.extensions.find((item) => item.type === "extension" && item.name === EXTENSION_NAME);

// get the current minute time, avoid two char with different time splitted with too many marks
const getMinuteTime = () => Math.round(new Date().getTime() / 1000 / 60) * 1000 * 60;

/**
 * accept or reject tracked changes for all content or just the selection
 * @param opType operation to apply
 * @param param a command props, so we can get the editor, tr prop
 * @returns null
 */
const changeTrack = (opType, param, sessionId) => {
  /**
   * get the range to deal, use selection default
   */
  const from = param.editor.state.selection.from;
  const to = param.editor.state.selection.to;
  /**
   * find all the mark ranges to deal and remove mark or remove content according by opType
   * if got accept all or reject all, just set 'from' to 0 and 'to' to content size
   * if got just a part range,
   */
  let markRanges = [];
  /**
   * deal a part and no selection contents, need to recognize the left mark near by cursor
   */
  if ((opType === TRACK_COMMAND_ACCEPT || opType === TRACK_COMMAND_REJECT) && from === to) {
    // detect left mark
    const isInsertBeforeCursor = isMarkActive(param.editor.state, MARK_INSERTION);
    const isDeleteBeforeCursor = isMarkActive(param.editor.state, MARK_DELETION);
    let leftRange;
    if (isInsertBeforeCursor) {
      leftRange = getMarkRange(param.editor.state.selection.$from, param.editor.state.doc.type.schema.marks.insertion);
    } else if (isDeleteBeforeCursor) {
      leftRange = getMarkRange(param.editor.state.selection.$from, param.editor.state.doc.type.schema.marks.deletion);
    }
    if (leftRange) {
      markRanges = getMarksBetween(leftRange.from, leftRange.to, param.editor.state.doc);
    }
  } else if (opType === TRACK_COMMAND_ACCEPT_ALL || opType === TRACK_COMMAND_REJECT_ALL) {
    // all editor content
    markRanges = getMarksBetween(0, param.editor.state.doc.content.size, param.editor.state.doc);
    // change the opType to normal
    opType = opType === TRACK_COMMAND_ACCEPT_ALL ? TRACK_COMMAND_ACCEPT : TRACK_COMMAND_REJECT;
  } else if (opType === TRACK_COMMAND_ACCEPT_BY_SESSION || opType === TRACK_COMMAND_REJECT_BY_SESSION) {
    // only marks created during the given session — used by features like Quick Edit so
    // accept/reject can never touch tracked changes that belong to a different flow.
    // a non-empty session id is required so we don't accidentally match legacy/untagged marks.
    if (!sessionId) {
      return false;
    }
    markRanges = getMarksBetween(0, param.editor.state.doc.content.size, param.editor.state.doc).filter(
      (markRange) => markRange.mark.attrs[SESSION_ID_ATTR] === sessionId,
    );
    opType = opType === TRACK_COMMAND_ACCEPT_BY_SESSION ? TRACK_COMMAND_ACCEPT : TRACK_COMMAND_REJECT;
  } else {
    // just the selection
    markRanges = getMarksBetween(from, to, param.editor.state.doc);
  }
  // just deal the track change nodes
  markRanges = markRanges.filter((markRange) => markRange.mark.type.name === MARK_DELETION || markRange.mark.type.name === MARK_INSERTION);
  if (!markRanges.length) {
    return false;
  }

  const currentTr = param.tr;
  /**
   * mark type and opType compose:
   * 1. accept with insert mark: remove insert mark
   * 2. accept with delete mark: remove content
   * 3. reject with insert mark: remove content
   * 4. reject with delete mark: remove delete mark
   * so
   * 1 and 4 need to remove mark
   * 2 and 3 need to remove content
   */
  // Note: offset was previously tracked here but never incremented (dead code). Removed.
  const removeInsertMark = param.editor.state.doc.type.schema.marks.insertion.create();
  const removeDeleteMark = param.editor.state.doc.type.schema.marks.deletion.create();
  let deletedStartIndex;
  let deleteEndIndex;
  let rejectDeleteCount = 0;
  let inlineEdit = false;
  let doubleSpaceParagraphs = false;
  markRanges.forEach((markRange) => {
    const isAcceptInsert = opType === TRACK_COMMAND_ACCEPT && markRange.mark.type.name === MARK_INSERTION;
    const isRejectDelete = opType === TRACK_COMMAND_REJECT && markRange.mark.type.name === MARK_DELETION;
    const isRejectInsert = opType === TRACK_COMMAND_REJECT && markRange.mark.type.name === MARK_INSERTION;
    const isAcceptDelete = opType === TRACK_COMMAND_ACCEPT && markRange.mark.type.name === MARK_DELETION;

    // get the closest paragraph node above the current paragraph node
    // non-leaf nodes have begin + end tokens (an extra index on each end), so 2 is subtracted
    const pos = markRange.from - 2 < 0 ? 0 : markRange.from - 2;
    const closestParentSiblingP = param.editor.state.doc.resolve(pos).parent;

    // get the direct parent
    const directParent = param.editor.state.doc.resolve(markRange.from).parent;

    if (isAcceptInsert) {
      // 1: remove insert mark but not text when accepting new content
      currentTr.removeMark(markRange.from, markRange.to, removeInsertMark.type);

      // we always want the deleting to start at the end of the last insert mark when accepting
      deletedStartIndex = markRange.to;

      // we need to get rid of the empty paragraph at the end of the insert mark
      // 2 is added to get us to the next non-leaf node (accounts for the begin and end tokens)
      deleteEndIndex = markRange.to + 2;
    } else if (isRejectDelete) {
      // 4: remove delete mark but not text when keeping old content (rejecting)
      currentTr.removeMark(markRange.from, markRange.to, removeDeleteMark.type);

      // account for removing extra newline when rejecting at the top of a document
      if (deletedStartIndex === 0 && rejectDeleteCount === 0) {
        deleteEndIndex = markRange.from;
      } else if (inlineEdit && closestParentSiblingP.content.size === 0) {
        // account for inline edits, need to remove extra whitespace above this mark
        deleteEndIndex = markRange.from;
      } else if (rejectDeleteCount === 0 && doubleSpaceParagraphs) {
        // account for users that are double spacing paragraphs
        deleteEndIndex = pos - 2 < 0 ? 0 : pos - 2; // include the closest parent sibling node
      } else if (rejectDeleteCount === 0) {
        // otherwise delete should extend to the beginning of the first delete mark's parent when rejecting
        deleteEndIndex = pos;
      }

      rejectDeleteCount++;
    } else if (isRejectInsert) {
      // 3: track index for deleting mark, text, and empty paragraph when rejecting new content
      if (deletedStartIndex === undefined) {
        if (closestParentSiblingP.content.size > 0) {
          // the closest parent sibling node has content, so we need to include it in the delete range
          deletedStartIndex = pos; // go back to the parent paragraph node

          const siblingContent = closestParentSiblingP.content;
          const directParentContent = directParent.content;

          // if the sibling p content and markRange content are the same, we are inline
          if (JSON.stringify(siblingContent) === JSON.stringify(directParentContent)) {
            inlineEdit = true;
            deletedStartIndex = markRange.from; // just go back to the start of the range.
          }
        } else {
          // the closest parent sibling node is empty, the user is double spacing paragraphs
          doubleSpaceParagraphs = true;
          deletedStartIndex = pos - 2 < 0 ? 0 : pos - 2; // include the closest parent sibling node
        }
      }
      deleteEndIndex = markRange.to;
    } else if (isAcceptDelete) {
      // 2: track index for deleting mark, text, and empty paragraph when keeping new content (accepting)
      if (deletedStartIndex === undefined) {
        deletedStartIndex = markRange.from - 4; // include the closest parent sibling node (the paragraph that gets inserted between insert and delete ranges)
      }
      deleteEndIndex = markRange.to;
    }
  });

  // if there is only new content, we need to remove the extra newline at the end of the insert mark
  if (markRanges.filter((markRange) => markRange.mark.type.name === MARK_DELETION).length === 0) {
    deleteEndIndex += 2; // include the end token of the last insert mark, and the begin token of the next node

    if (inlineEdit) {
      // if we are inline editing, we need to remove additional extra whitespace
      deleteEndIndex += 2;
    }
  }

  // gets rid of extra whitespace from empty paragraphs
  if (deletedStartIndex !== undefined && deleteEndIndex !== undefined) {
    // Map positions through transaction to account for earlier mutations (removeMark steps)
    deletedStartIndex = currentTr.mapping.map(deletedStartIndex, -1);
    deleteEndIndex = currentTr.mapping.map(deleteEndIndex, 1);

    // Clamp to valid document bounds
    const docSize = currentTr.doc.content.size;
    deletedStartIndex = Math.max(0, Math.min(deletedStartIndex, docSize));
    deleteEndIndex = Math.max(0, Math.min(deleteEndIndex, docSize));

    if (deletedStartIndex < deleteEndIndex) {
      currentTr.deleteRange(deletedStartIndex, deleteEndIndex);
    }
  }

  if (currentTr.steps.length) {
    // set a custom meta to tail Our TrackChangeExtension to ignore this change
    // TODO: is there any official field to do this?
    currentTr.setMeta("trackManualChanged", true);
    // Tiptap's command manager dispatches this transaction after the command returns.
    // Applying it here first makes that later dispatch reuse a stale transaction.
  }
  return true;
};

/**
 * lets us know if we have insert or delete marks in the editor
 * @param param, so we can get the editor
 * @returns boolean, true if insert or delete marks exist
 */
const hasTrackedChanges = (param) => {
  const { editor } = param;

  // get all marks in the editor
  let markRanges = getMarksBetween(0, editor.state.doc.content.size, editor.state.doc);

  // filter out any marks that are not insert or delete marks
  markRanges = markRanges.filter((markRange) => markRange.mark.type.name === MARK_DELETION || markRange.mark.type.name === MARK_INSERTION);

  return markRanges.length > 0;
};

// @ts-ignore
/**
 * TODO: some problems to fix or feature to implement
 * 1. when delete content includes two and more paragraphs, cannot mark the new paragraph as insert mark, because the mark is inline, can we add global attrs?
 * 2. when delete content includes two and more paragraphs, connot ignore the insert mark inside the content. Currently, the insert mark is marked as deleted. But it need to be delete directly.
 * 3. select two chars and inout a chinese char, the new char was input with wrong position. (fixed by stop input action)
 * 4. how to toggle to "hide" mode and can record the change ranges too, just look likes the office word
 */
export const TrackChangeExtension = Extension.create({
  name: EXTENSION_NAME,
  onCreate() {
    if (this.options.onStatusChange) {
      this.options.onStatusChange(this.options.enabled);
    }
  },
  addExtensions() {
    return [InsertionMark, DeletionMark];
  },
  addCommands: () => {
    return {
      setTrackChangeStatus: (enabled) => (param) => {
        const thisExtension = getSelfExt(param.editor);
        thisExtension.options.enabled = enabled;
        if (thisExtension.options.onStatusChange) {
          thisExtension.options.onStatusChange(thisExtension.options.enabled);
        }
        return false;
      },
      toggleTrackChangeStatus: () => (param) => {
        const thisExtension = getSelfExt(param.editor);
        thisExtension.options.enabled = !thisExtension.options.enabled;
        if (thisExtension.options.onStatusChange) {
          thisExtension.options.onStatusChange(thisExtension.options.enabled);
        }
        return false;
      },
      getTrackChangeStatus: () => (param) => {
        const thisExtension = getSelfExt(param.editor);
        return thisExtension.options.enabled;
      },
      hasTrackedChanges: () => (param) => {
        return hasTrackedChanges(param);
      },
      acceptChange: () => (param) => {
        return changeTrack(TRACK_COMMAND_ACCEPT, param);
      },
      acceptAllChanges: () => (param) => {
        return changeTrack(TRACK_COMMAND_ACCEPT_ALL, param);
      },
      rejectChange: () => (param) => {
        return changeTrack(TRACK_COMMAND_REJECT, param);
      },
      rejectAllChanges: () => (param) => {
        return changeTrack(TRACK_COMMAND_REJECT_ALL, param);
      },
      acceptChangesBySession: (sessionId) => (param) => {
        return changeTrack(TRACK_COMMAND_ACCEPT_BY_SESSION, param, sessionId);
      },
      rejectChangesBySession: (sessionId) => (param) => {
        return changeTrack(TRACK_COMMAND_REJECT_BY_SESSION, param, sessionId);
      },
      setEditSessionId: (sessionId) => (param) => {
        const thisExtension = getSelfExt(param.editor);
        thisExtension.options.dataOpEditSessionId = sessionId || "";
        return false;
      },
      updateOpUserOption: (opUserId, opUserNickname) => (param) => {
        const thisExtension = getSelfExt(param.editor);
        thisExtension.options.dataOpUserId = opUserId;
        thisExtension.options.dataOpUserNickname = opUserNickname;
        return false;
      },
    };
  },
  // @ts-ignore
  onSelectionUpdate(p) {
    // log the status for debug
    LOG_ENABLED && console.log("selection and input status", p.transaction.selection.from, p.transaction.selection.to, p.editor.view.composing);
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("track changes"), // "composing-check"
        props: {
          handleDOMEvents: {
            compositionstart: (_event) => {
              LOG_ENABLED && console.log("start chinese input");
              // start and update will fire same time
              isStartChineseInput = true;
            },
            compositionupdate: (_event) => {
              LOG_ENABLED && console.log("chinese input continue");
              composingStatus = IME_STATUS_CONTINUE;
            },
          },
        },
      }),
    ];
  },
  // @ts-ignore
  onTransaction: (props) => {
    const { transaction, editor } = props;
    // chinese input status check
    const isChineseStart = isStartChineseInput && composingStatus === IME_STATUS_CONTINUE;
    const isChineseInputting = !isStartChineseInput && composingStatus === IME_STATUS_CONTINUE;
    const isNormalInput = composingStatus === IME_STATUS_NORMAL; // not ime mode
    composingStatus = IME_STATUS_NORMAL; // reset for next change
    isStartChineseInput = false;
    /**
     * normal ime mode start, we can ignore the input and deal change when last confirm
     * but what if it has a selection before? the selected content will loss for real. so we have to deal with it
     */
    // no change, ignore
    if (!transaction.docChanged) {
      return;
    }
    // check if it is change by accept or reject
    if (transaction.getMeta("trackManualChanged")) {
      return;
    }
    // if this is a redo or undo, ignore it.
    if (transaction.getMeta("history$")) {
      return;
    }
    // Ignore all collaboration sync transactions (remote OR local persistence
    // replay). They are not direct user edits, and processing them here can
    // strip insertion marks during remount/doc-switch hydration.
    const syncMeta = transaction.getMeta("y-sync$");
    if (syncMeta) {
      LOG_ENABLED && console.log("sync from origin", syncMeta);
      return;
    }
    // has no real step
    if (!transaction.steps.length) {
      LOG_ENABLED && console.log("none content change");
      return;
    }
    // check if this tr was applied to editor: vue2 and vue3 have different result
    const isThisTrApplied = transaction.before !== editor.state.tr.doc;
    const thisExtension = getSelfExt(editor);
    const trackChangeEnabled = thisExtension.options.enabled;
    LOG_ENABLED && console.warn("Content Changes", transaction.steps.length, transaction);
    /**
     * Two main process
     *
     * 1. When Status disabled
     *  a. the new insert content cannot be wrapped with insert or delete mark (avoid the original auto style by browser)
     *  b. consider the ime mode
     *  c. make the cursor pos right
     *  d. can "remove the auto-used style" and "user edit" op be a single operation to history?
     *
     * 2. When Status Enabled(with more complex process)
     *  a. mark the new content with insert
     *  b. remove auto-used delete mark for new content
     *  c. readd the deleted normal content and mark as delete
     *  d. readd the deleted "delete char"
     *  e. if "insert char" be deleted, just let it go
     *  f. correct the final cursor position, too many cases to consider
     *  h. careful with ime mode
     *  i. ignore history updates
     *
     * Test Case:
     * 1. enabled: input char
     * 1. enabled: delete normal char
     * 1. enabled: delete the 'delete' char
     * 1. enabled: input after the 'delete' char
     * 1. enabled: delete the 'insert' char
     * 1. enabled: input after the 'insert' char
     * 1. enabled: make a selection and delete then
     * 1. enabled: select normal char and input new text
     * 1. enabled: select 'insert' chars and input new text
     * 1. enabled: ime input after normal char
     * 1. enabled: ime input after insert char
     * 1. enabled: ime input after delete char
     * 1. enabled: ime input when select normal chars
     * 1. enabled: ime input when select insert chars
     * 1. enabled: ime input when select delete chars
     * 1. enabled: ime input when select both insert and delete chars
     *
     *
     * 2. disabled: input normal char
     * 2. disabled: input after insert char
     * 2. disabled: input after delete char
     */
    // copy the step, avoid the change the origin step info
    let allSteps;
    try {
      allSteps = transaction.steps.map((step) => Step.fromJSON(editor.state.doc.type.schema, step.toJSON()));
    } catch (error) {
      // Ignore the error and exit early
      return;
    }
    LOG_ENABLED && console.log("allSteps", allSteps);
    // latest cursor pos
    // TODO: recognize selection direction and backspace/delete to get calculate new cursor pos
    const currentNewPos = transaction.selection.from;
    LOG_ENABLED && console.log("currentNewPos", currentNewPos);

    // cursor offset, try to reset the pos in the end process
    let posOffset = 0;
    let hasAddAndDelete = false;
    allSteps.forEach((step, _index, _arr) => {
      if (step instanceof ReplaceStep) {
        // loss chars
        let delCount = 0;
        if (step.from !== step.to) {
          /**
           * means some content will be removed, but in this extension, we will reAdd it back
           * note: if the old content is already 'insert', just let it go.
           */
          const slice = transaction.docs[_index].slice(step.from, step.to);
          slice.content.forEach((node) => {
            const isInsertNode = node.marks.find((m) => m.type.name === MARK_INSERTION);
            if (!isInsertNode) {
              // sum the none-insert mark size
              // TODO: nodeSize or size filed? what if the node is a image or some other custom node
              delCount += node.nodeSize;
            }
          });
        }
        posOffset += delCount;
        // added chars in this step
        const newCount = step.slice ? step.slice.size : 0;
        if (newCount && delCount) {
          // if only add or del, don't need to change selection, it's already right
          hasAddAndDelete = true;
        }
      }
    });
    if (isNormalInput) {
      // none ime mode
      if (!hasAddAndDelete) {
        // just delete or add, no need to correct cursor
        posOffset = 0;
      }
    } else if (isChineseStart) {
      // first chinese char
      if (hasAddAndDelete) {
        /**
         * input chinese char when a selection content exist
         * need the readd the selected chars, so we need update the cursor
         * but we cannot change the cursor in "inputting time", ,,,,need some more test
         */
        // posOffset -= 1
      } else {
        // just new content, ime software will make it right
        posOffset = 0;
      }
    } else if (isChineseInputting) {
      // chinese input, at least two chars, no need to update cursor
      posOffset = 0;
    }
    // TODO: if there has multiple replace steps,we can ignore the cursor
    LOG_ENABLED &&
      console.table({
        hasAddAndDelete,
        isNormalInput,
        isChineseStart,
        isChineseInputting,
        posOffset,
      });

    // get the correct tr to manipulate
    // tr had been applied in vue2, and not in vue3
    // so we get a new tr in vue2 by "editor.state.tr"
    // TODO: if we use vue2, the cursor correction need to be more tested
    const newChangeTr = isThisTrApplied ? editor.state.tr : transaction;

    let reAddOffset = 0;
    allSteps.forEach((step, index) => {
      if (step instanceof ReplaceStep) {
        const invertedStep = step.invert(transaction.docs[index]);
        if (step.slice.size) {
          const insertionMark = editor.state.doc.type.schema.marks.insertion.create({
            "data-op-user-id": thisExtension.options.dataOpUserId,
            "data-op-user-nickname": thisExtension.options.dataOpUserNickname,
            "data-op-date": getMinuteTime(),
            [SESSION_ID_ATTR]: thisExtension.options.dataOpEditSessionId || "",
          });
          const deletionMark = editor.state.doc.type.schema.marks.deletion.create();
          const from = step.from + reAddOffset;
          const to = step.from + reAddOffset + step.slice.size;
          if (trackChangeEnabled) {
            // add insert mark to new content
            newChangeTr.addMark(from, to, insertionMark);
          } else {
            // if disabled this extension, remove auto-used track mark for new content
            newChangeTr.removeMark(from, to, insertionMark.type);
          }
          // remove auto-used delete mark for new content anyway
          newChangeTr.removeMark(from, to, deletionMark.type);
        }
        if (step.from !== step.to && trackChangeEnabled) {
          LOG_ENABLED && console.log("find content to readd", step);
          // get the reverted step, so we can know what content is deleted in this step and readd then
          // make sure use related doc in transaction. every step has the new doc with same order
          // TODO: is there difference between vue2 and vue3
          const skipSteps = [];
          // collect the content we need to ignore readd, because some content is insert mark before, there data need to be allowed delete
          LOG_ENABLED && console.log("invertedStep", invertedStep);
          const reAddStep = new ReplaceStep(
            invertedStep.from + reAddOffset,
            invertedStep.from + reAddOffset,
            invertedStep.slice,
            // @ts-ignore: what is internal means
            invertedStep.structure
          );
          // make a empty step to replace the original "INSERT" mark, because these content don't need to readd
          // the slice content maybe a TextNode or other node with any child content
          // so we need to travel all the content to find the "INSERT" mark
          let addedEmptyOffset = 0; // when empty step is added, record the offset to correct the next empty step, because the content will b shorter than the current when create next empty step
          const travelContent = (content, parentOffset) => {
            content.forEach((node, offset) => {
              if (!node || typeof node.nodeSize !== "number") return;
              const start = parentOffset + offset;
              const end = start + node.nodeSize;
              if (node.content && node.content.size) {
                // this node has child content, need to travel
                travelContent(node.content, start + 1);
              } else {
                // this node is a text node, or a node without child content
                const marks = Array.isArray(node.marks) ? node.marks : [];
                const currentSessionId = thisExtension.options.dataOpEditSessionId || "";
                const insertionMark = marks.find((m) => m?.type?.name === MARK_INSERTION);
                if (insertionMark && insertionMark.attrs[SESSION_ID_ATTR] === currentSessionId) {
                  // Only skip content inserted by the CURRENT session — it was just added
                  // and should vanish on delete, not show as struck-through.
                  // Stale marks from other sessions represent original document content
                  // that must be preserved for reject.
                  skipSteps.push(new ReplaceStep(start - addedEmptyOffset, end - addedEmptyOffset, Slice.empty));
                  addedEmptyOffset += node.nodeSize;
                  reAddOffset -= node.nodeSize;
                }
              }
            });
          };
          travelContent(invertedStep.slice.content, invertedStep.from);
          reAddOffset += invertedStep.slice.size;
          // apply readd step action
          let appliedSliceSize = invertedStep.slice.size;
          let usedFlatFallback = false;
          try {
            newChangeTr.step(reAddStep);
          } catch (reAddErr) {
            // The reAddStep failed with "Inconsistent open depths" — the slice has
            // non-zero openStart/openEnd from a cross-paragraph deletion. Retry with a
            // flattened slice (open depths = 0) to preserve the content for reject.
            const flatSlice = new Slice(invertedStep.slice.content, 0, 0);
            const flatStep = new ReplaceStep(reAddStep.from, reAddStep.from, flatSlice, false);
            try {
              newChangeTr.step(flatStep);
              appliedSliceSize = flatSlice.size;
              usedFlatFallback = true;
              // Adjust reAddOffset: flat slice may be larger than original (no openStart/openEnd)
              reAddOffset += flatSlice.size - invertedStep.slice.size;
            } catch (_flatErr) {
              // Both steps failed — content cannot be re-added for reject.
              reAddOffset -= invertedStep.slice.size;
              if (thisExtension.options.onStrayMarksCleaned) {
                try {
                  thisExtension.options.onStrayMarksCleaned({
                    count: 0,
                    error: reAddErr.message,
                    currentSessionId: thisExtension.options.dataOpEditSessionId || "",
                  });
                } catch (_e) {
                  // Telemetry must not break the editor.
                }
              }
              return; // skip to next step in allSteps.forEach
            }
            if (thisExtension.options.onStrayMarksCleaned) {
              try {
                thisExtension.options.onStrayMarksCleaned({
                  count: 0,
                  error: reAddErr.message,
                  currentSessionId: thisExtension.options.dataOpEditSessionId || "",
                });
              } catch (_e) {
                // Telemetry must not break the editor.
              }
            }
          }
          const { from } = reAddStep;
          const to = from + appliedSliceSize;
          // add delete mark for readd content
          newChangeTr.addMark(
            from,
            to,
            newChangeTr.doc.type.schema.marks.deletion.create({
              "data-op-user-id": thisExtension.options.dataOpUserId,
              "data-op-user-nickname": thisExtension.options.dataOpUserNickname,
              "data-op-date": getMinuteTime(),
              [SESSION_ID_ATTR]: thisExtension.options.dataOpEditSessionId || "",
            })
          );
          // Defensive: strip any pre-existing insertion marks from the re-added range.
          // Without this, content that already carried an insertion mark (e.g. stale residue
          // from a previous Quick Edit that didn't clean up) would end up with BOTH an
          // insertion and a deletion mark, rendering as green text with red strikethrough.
          // Telemetry: surface how often this stray-mark cleanup runs so we can monitor for
          // upstream issues where Quick Edit sessions are leaving residue behind.
          if (thisExtension.options.onStrayMarksCleaned) {
            const strayMarks = getMarksBetween(from, to, newChangeTr.doc).filter(
              (mr) => mr.mark.type.name === MARK_INSERTION,
            );
            if (strayMarks.length > 0) {
              try {
                thisExtension.options.onStrayMarksCleaned({
                  count: strayMarks.length,
                  staleSessionIds: Array.from(
                    new Set(strayMarks.map((mr) => mr.mark.attrs[SESSION_ID_ATTR] || "")),
                  ),
                  currentSessionId: thisExtension.options.dataOpEditSessionId || "",
                });
              } catch (_e) {
                // Telemetry must not break the editor — swallow callback errors.
              }
            }
          }
          newChangeTr.removeMark(from, to, newChangeTr.doc.type.schema.marks.insertion);
          if (!usedFlatFallback) {
            skipSteps.forEach((step) => {
              // delete the content if it is already with insert mark
              try {
                newChangeTr.step(step);
              } catch (_skipErr) {
                // Position may have become stale after the reAddStep modified the document
                // structure. Silently skip — the content will remain but won't have a
                // conflicting insertion+deletion mark pair.
              }
            });
          }
        }
        const newState = editor.state.apply(newChangeTr);
        editor.view.updateState(newState);
        // TODO: if there has cursor change action, can there change be merged to one tr with one updateState?
      }
    });

    // calculate new cursor postion
    let finalNewPos = trackChangeEnabled ? currentNewPos + posOffset : currentNewPos;
    if (finalNewPos > editor.view.state.doc.content.size) {
      finalNewPos = editor.view.state.doc.content.size;
    }
    if (trackChangeEnabled) {
      const trWithChange = editor.view.state.tr;
      trWithChange.setSelection(TextSelection.create(editor.view.state.doc, finalNewPos));
      const newStateWithNewSelection = editor.view.state.apply(trWithChange);
      LOG_ENABLED && console.log("update cursor", finalNewPos);
      editor.view.updateState(newStateWithNewSelection);
    }
    if (isChineseStart && hasAddAndDelete && trackChangeEnabled) {
      // if the first chinese input and have some content selected
      // just make it stop by blur action
      // delete the selection and use just need paste the new content again
      editor.commands.deleteSelection();
      editor.commands.blur();
      setTimeout(() => {
        // focus again
        editor.commands.focus();
      }, 100);
    }
  },
});

export default TrackChangeExtension;

/* MIT License

Copyright (c) 2023 Ray Chen

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
